/**
 * Metadata Service
 * 
 * Service for fetching and caching EspoCRM metadata.
 * Provides access to entity definitions, layouts, navigation, and ACL.
 * 
 * This is a generic service that can be used across all tenants.
 */

import { EspoCrmMetadata, UserInfoWithAcl, EntityDef, Layout, ListColumn } from '../types/espoCrm';

// Type for API instance with required endpoints
export interface MetadataApi {
  endpoints: {
    getMetadata: {
      initiate: (arg: undefined, options?: { forceRefetch?: boolean }) => any;
    };
    getUserInfo: {
      initiate: (arg: undefined, options?: { forceRefetch?: boolean }) => any;
    };
    getListLayout?: {
      initiate: (entityName: string) => any;
    };
    getDetailLayout?: {
      initiate: (entityName: string) => any;
    };
    getI18n?: {
      initiate: (arg: { default: boolean }, options?: { forceRefetch?: boolean }) => any;
    };
  };
}

// Cache for metadata (per API instance)
const metadataCache = new WeakMap<MetadataApi, EspoCrmMetadata>();
const userInfoCache = new WeakMap<MetadataApi, UserInfoWithAcl>();
const metadataPromise = new WeakMap<MetadataApi, Promise<EspoCrmMetadata>>();
const userInfoPromise = new WeakMap<MetadataApi, Promise<UserInfoWithAcl>>();

/**
 * Fetch metadata from EspoCRM API using RTK Query
 */
export async function fetchMetadata(api: MetadataApi): Promise<EspoCrmMetadata> {
  // Return cached metadata if available
  const cached = metadataCache.get(api);
  if (cached) {
    return cached;
  }

  // Return existing promise if fetch is in progress
  const existingPromise = metadataPromise.get(api);
  if (existingPromise) {
    return existingPromise;
  }

  // Create new fetch promise using RTK Query
  const promise = (async () => {
    try {
      // Lazy import store to avoid circular dependency
      const { store } = await import('src/store');
      const result = await store.dispatch(
        api.endpoints.getMetadata.initiate(undefined, { forceRefetch: true })
      ) as any;

      if (result.error) {
        throw new Error(`Failed to fetch metadata: ${result.error}`);
      }

      if (!result.data) {
        throw new Error('Metadata data is undefined');
      }

      metadataCache.set(api, result.data);
      return result.data;
    } catch (error) {
      metadataPromise.delete(api); // Reset promise on error
      throw error;
    }
  })();

  metadataPromise.set(api, promise);
  return promise;
}

/**
 * Fetch user info with ACL from EspoCRM API using RTK Query
 */
export async function fetchUserInfo(api: MetadataApi): Promise<UserInfoWithAcl> {
  // Return cached user info if available
  const cached = userInfoCache.get(api);
  if (cached) {
    return cached;
  }

  // Return existing promise if fetch is in progress
  const existingPromise = userInfoPromise.get(api);
  if (existingPromise) {
    return existingPromise;
  }

  // Create new fetch promise using RTK Query
  const promise = (async () => {
    try {
      // Lazy import store to avoid circular dependency
      const { store } = await import('src/store');
      const result = await store.dispatch(
        api.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true })
      ) as any;

      if (result.error) {
        throw new Error(`Failed to fetch user info: ${result.error}`);
      }

      if (!result.data) {
        throw new Error('User info data is undefined');
      }

      // Map EspoCRM response to UserInfoWithAcl format
      const rawData = result.data as any;
      
      // EspoCRM's /api/v1/App/user returns permissions in acl.table
      // Map acl.table to permissions for easier access
      let permissions = rawData.permissions;
      
      // If permissions are not at root level, check acl.table
      if (!permissions && rawData.acl?.table) {
        permissions = rawData.acl.table;
      }
      
      // If permissions are still not found, check in app object
      if (!permissions && rawData.app) {
        permissions = rawData.app.acl?.table || rawData.app.acl || rawData.app.permissions;
      }
      
      const mappedUserInfo: UserInfoWithAcl = {
        ...rawData,
        // Map acl.table to permissions for easier access in ACL service
        permissions: permissions || {},
        // Ensure settings is preserved (contains tabList)
        settings: rawData.settings || {},
      };

      userInfoCache.set(api, mappedUserInfo);
      return mappedUserInfo;
    } catch (error) {
      userInfoPromise.delete(api); // Reset promise on error
      throw error;
    }
  })();

  userInfoPromise.set(api, promise);
  return promise;
}

/**
 * Get metadata (from cache or fetch)
 */
export async function getMetadata(api: MetadataApi): Promise<EspoCrmMetadata> {
  const cached = metadataCache.get(api);
  if (cached) {
    return cached;
  }
  return fetchMetadata(api);
}

/**
 * Get user info (from cache or fetch)
 */
export async function getUserInfo(api: MetadataApi): Promise<UserInfoWithAcl> {
  const cached = userInfoCache.get(api);
  if (cached) {
    return cached;
  }
  return fetchUserInfo(api);
}

/**
 * Get entity definition
 */
export function getEntityDef(metadata: EspoCrmMetadata, entityName: string): EntityDef | undefined {
  return metadata.entityDefs?.[entityName];
}

/**
 * Get list layout for an entity
 * If not found in metadata, will attempt to fetch from API
 */
export async function getListLayout(
  api: MetadataApi,
  metadata: EspoCrmMetadata, 
  entityName: string
): Promise<ListColumn[] | undefined> {
  // First try to get from metadata
  const layoutFromMetadata = metadata.client?.layouts?.[entityName]?.list;
  if (layoutFromMetadata) {
    return layoutFromMetadata;
  }

  // If not in metadata, fetch from API endpoint: /api/v1/{entityName}/layout/list
  if (!api.endpoints.getListLayout) {
    return undefined;
  }

  try {
    const { store } = await import('src/store');
    const result = await store.dispatch(
      api.endpoints.getListLayout.initiate(entityName)
    ) as any;

    if (result.error) {
      console.warn(`Failed to fetch list layout for ${entityName}:`, result.error);
      return undefined;
    }

    if (result.data) {
      // EspoCRM returns layout as an array of columns
      // The response might be directly an array or wrapped in an object
      let layoutData = result.data;
      
      // If it's wrapped, try to extract the array
      if (typeof layoutData === 'object' && !Array.isArray(layoutData)) {
        // Try common property names
        layoutData = (layoutData as any).list || (layoutData as any).columns || (layoutData as any).data || layoutData;
      }
      
      if (Array.isArray(layoutData)) {
        console.log(`Successfully fetched list layout for ${entityName}:`, layoutData.length, 'columns');
        return layoutData as ListColumn[];
      } else {
        console.warn(`List layout for ${entityName} is not in expected format:`, layoutData);
      }
    }
  } catch (error) {
    console.warn(`Failed to fetch list layout for ${entityName}:`, error);
  }

  return undefined;
}

/**
 * Get detail layout for an entity
 * If not found in metadata, will attempt to fetch from API
 */
export async function getDetailLayout(
  api: MetadataApi,
  metadata: EspoCrmMetadata, 
  entityName: string
): Promise<Layout | undefined> {
  // First try to get from metadata
  const layoutFromMetadata = metadata.client?.layouts?.[entityName]?.detail;
  if (layoutFromMetadata) {
    return layoutFromMetadata;
  }

  // If not in metadata, fetch from API endpoint: /api/v1/{entityName}/layout/detail
  if (!api.endpoints.getDetailLayout) {
    return undefined;
  }

  try {
    const { store } = await import('src/store');
    const result = await store.dispatch(
      api.endpoints.getDetailLayout.initiate(entityName)
    ) as any;

    if (result.error) {
      console.warn(`Failed to fetch detail layout for ${entityName}:`, result.error);
      return undefined;
    }

    if (result.data) {
      // EspoCRM returns layout as an array of panels
      let layoutData = result.data;
      
      // If it's wrapped, try to extract the array
      if (typeof layoutData === 'object' && !Array.isArray(layoutData)) {
        layoutData = (layoutData as any).detail || (layoutData as any).data || layoutData;
      }
      
      if (Array.isArray(layoutData)) {
        console.log(`Successfully fetched detail layout for ${entityName}`);
        return layoutData as Layout;
      } else {
        console.warn(`Detail layout for ${entityName} is not in expected format:`, layoutData);
      }
    }
  } catch (error) {
    console.warn(`Failed to fetch detail layout for ${entityName}:`, error);
  }

  return undefined;
}

/**
 * Get edit layout for an entity
 * If not found in metadata, will attempt to fetch from API (uses detail endpoint)
 */
export async function getEditLayout(
  api: MetadataApi,
  metadata: EspoCrmMetadata, 
  entityName: string
): Promise<Layout | undefined> {
  // First try to get from metadata
  const layoutFromMetadata = metadata.client?.layouts?.[entityName]?.edit || metadata.client?.layouts?.[entityName]?.detail;
  if (layoutFromMetadata) {
    return layoutFromMetadata;
  }

  // If not in metadata, fetch detail layout from API (edit uses same endpoint as detail)
  return getDetailLayout(api, metadata, entityName);
}

/**
 * Get navigation menu
 */
export function getNavigation(metadata: EspoCrmMetadata): any {
  return metadata.navigation || metadata.client?.menu || [];
}

/**
 * Get all entity names
 */
export function getEntityNames(metadata: EspoCrmMetadata): string[] {
  return Object.keys(metadata.entityDefs || {});
}

/**
 * Clear metadata cache (useful for testing or refresh)
 */
export function clearMetadataCache(api: MetadataApi): void {
  metadataCache.delete(api);
  metadataPromise.delete(api);
  userInfoCache.delete(api);
  userInfoPromise.delete(api);
}

/**
 * Initialize metadata service
 * Fetches both metadata and user info
 */
export async function initializeMetadataService(api: MetadataApi): Promise<{
  metadata: EspoCrmMetadata;
  userInfo: UserInfoWithAcl;
}> {
  const [metadata, userInfo] = await Promise.all([
    fetchMetadata(api),
    fetchUserInfo(api),
  ]);

  return { metadata, userInfo };
}
